import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sb } from '../supabase';
import { CATEGORIES } from '../lib/supplierDashboardConstants';
import { publicRfqStrings, supplierDocsErrorText } from './publicRfqStrings';

// Stage 1 of the two-bar verification model — the VISIBILITY bar.
// See CLAUDE.md golden rule 2a and migration 20260918183522.
//
// This is where a supplier lands the moment they submit a quote through a
// public RFQ link. It asks for three things and nothing else: business
// licence, one contact channel, product category. The eight-field payout file
// lives on the dashboard's verification tab and is NOT duplicated here — the
// whole point of the two bars is that having your quote looked at should not
// cost what getting paid costs.
//
// Deliberately absent: any personal ID document. PIPL exposure for zero
// verification value at a visibility-grade gate (owner, 2026-09-18).
//
// Skipping is a supported outcome, not a failure path. The quote is already
// saved and the account already exists; the copy says so, because the fear
// that stops people here is that leaving loses their work.

const wrap = { minHeight: 'var(--app-dvh)', paddingTop: 'var(--page-top-offset)', background: 'var(--bg-base)' };
const shellBase = { maxWidth: 620, margin: '0 auto', padding: '40px 20px 80px' };
const card = { background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 24, marginBottom: 20 };
const label = { display: 'block', fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 6 };
const hint = { margin: '4px 0 0', fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.6 };
const input = { width: '100%', padding: '11px 13px', borderRadius: 8, border: '1px solid var(--border-subtle)', background: 'var(--bg-base)', color: 'var(--text-primary)', fontSize: 15, boxSizing: 'border-box' };
const btn = { width: '100%', padding: '14px 18px', borderRadius: 8, border: 'none', background: 'var(--text-primary)', color: 'var(--bg-base)', fontSize: 15, cursor: 'pointer' };
const ghost = { width: '100%', marginTop: 10, padding: '11px', background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: 14, cursor: 'pointer' };
const errText = { color: 'var(--danger, #c0392b)', fontSize: 14, lineHeight: 1.7, margin: '12px 0 0' };

export default function SupplierVerifyDocs({ lang }) {
  const nav = useNavigate();
  const t = publicRfqStrings(lang);
  // Golden rule 3: trilingual with RTL. This page is the second half of the
  // public RFQ flow and inherits its audience, so it gets the same treatment.
  const isAr = lang === 'ar';
  const shell = { ...shellBase, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' };
  const cats = (CATEGORIES[lang] || CATEGORIES.en || CATEGORIES.ar || []).filter((c) => c.val !== 'all');

  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);

  const [licence, setLicence] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [wechat, setWechat] = useState('');
  const [speciality, setSpeciality] = useState('');
  const [tradeLink, setTradeLink] = useState('');

  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    const { data: sess } = await sb.auth.getSession();
    const uid = sess?.session?.user?.id;
    if (!uid) {
      // No session: this page has nothing to show and nothing to save. Send
      // them to sign in and come back, rather than rendering a form whose
      // every write will fail.
      nav(`/login/supplier?next=${encodeURIComponent('/supplier/verify')}`, { replace: true });
      return;
    }
    setUserId(uid);

    const { data, error } = await sb.from('profiles')
      .select('status, license_photo, whatsapp, wechat, speciality, trade_link')
      .eq('id', uid).maybeSingle();

    if (error) {
      console.error('[SupplierVerifyDocs] profile read failed:', error.code || '', error.message || error);
    } else if (data) {
      // Prefill from what signup already collected — speciality in particular
      // is required at registration, so most arrivals have it and see one
      // fewer thing to do.
      setLicence(data.license_photo || '');
      setWhatsapp(data.whatsapp || '');
      setWechat(data.wechat || '');
      setSpeciality(data.speciality || '');
      setTradeLink(data.trade_link || '');
      if (['verified', 'verification_under_review'].includes(String(data.status || '').toLowerCase())) {
        setDone(true);
      }
    }
    setLoading(false);
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  const uploadLicence = async (file) => {
    if (!file || !userId) return;
    setErr('');
    setUploading(true);
    // Same bucket, same path shape as the dashboard uploader, so one supplier's
    // documents stay in one place whichever surface they used.
    const ext = file.name.split('.').pop() || (file.type === 'application/pdf' ? 'pdf' : 'jpg');
    const path = `${userId}/license_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const { error: upErr } = await sb.storage.from('supplier-docs').upload(path, file, { upsert: true });
    if (upErr) {
      setUploading(false);
      console.error('[SupplierVerifyDocs] licence upload failed:', upErr.message || upErr);
      setErr(t.verifyUploadFailed);
      return;
    }
    const { error: saveErr } = await sb.from('profiles').update({ license_photo: path }).eq('id', userId);
    setUploading(false);
    if (saveErr) {
      console.error('[SupplierVerifyDocs] licence save failed:', saveErr.code || '', saveErr.message || saveErr);
      setErr(t.verifyUploadFailed);
      return;
    }
    setLicence(path);
  };

  const submit = async () => {
    setErr('');
    setBusy(true);

    // Save the fields first, then let the RPC judge the row — the same shape
    // the dashboard uses, so there is one way this works rather than two.
    const { error: saveErr } = await sb.from('profiles').update({
      whatsapp: whatsapp.trim() || null,
      wechat: wechat.trim() || null,
      speciality: speciality || null,
      trade_link: tradeLink.trim() || null,
    }).eq('id', userId);

    if (saveErr) {
      setBusy(false);
      console.error('[SupplierVerifyDocs] profile save failed:', saveErr.code || '', saveErr.message || saveErr);
      setErr(t.docsErrors.FALLBACK);
      return;
    }

    const { error } = await sb.rpc('submit_supplier_docs');
    setBusy(false);

    if (error) {
      // Log the provider's message (security rule 4), render a sentence.
      console.error('[SupplierVerifyDocs] submit_supplier_docs failed:', error.code || '', error.message || error);
      setErr(supplierDocsErrorText(error, t));
      return;
    }
    setDone(true);
  };

  if (loading) {
    return <div style={wrap} dir={isAr ? 'rtl' : 'ltr'}><div style={shell}><p style={{ color: 'var(--text-tertiary)' }}>{t.stateLoading}</p></div></div>;
  }

  if (done) {
    return (
      <div style={wrap} dir={isAr ? 'rtl' : 'ltr'}><div style={shell}>
        <div style={card}>
          <h1 style={{ margin: '0 0 12px', fontSize: 26, fontWeight: 400, color: 'var(--text-primary)' }}>{t.verifyDoneTitle}</h1>
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--text-secondary)' }}>{t.verifyDoneBody}</p>
        </div>
        <button style={btn} onClick={() => nav('/dashboard')}>{t.verifyDoneCta}</button>
      </div></div>
    );
  }

  return (
    <div style={wrap} dir={isAr ? 'rtl' : 'ltr'}><div style={shell}>
      <h1 style={{ margin: '0 0 10px', fontSize: 28, fontWeight: 300, color: 'var(--text-primary)' }}>{t.verifyTitle}</h1>
      <p style={{ margin: '0 0 24px', fontSize: 15, lineHeight: 1.8, color: 'var(--text-secondary)' }}>{t.verifyBody}</p>

      <div style={card}>
        <div style={{ marginBottom: 20 }}>
          <span style={label}>{t.verifyLicence}</span>
          <label style={{ ...btn, display: 'block', textAlign: 'center', opacity: uploading ? 0.6 : 1 }}>
            {uploading ? t.verifyUploading : licence ? t.verifyReplace : t.verifyUpload}
            <input
              type="file"
              accept="image/*,.pdf"
              style={{ display: 'none' }}
              disabled={uploading}
              onChange={async (e) => { await uploadLicence(e.target.files?.[0]); e.target.value = ''; }}
            />
          </label>
          <p style={hint}>{licence ? t.verifyUploaded : t.verifyLicenceHint}</p>
        </div>

        <div style={{ marginBottom: 6 }}>
          <span style={label}>{t.verifyContact}</span>
          <input
            style={{ ...input, marginBottom: 8 }}
            placeholder={t.verifyWhatsapp}
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
          />
          <input
            style={input}
            placeholder={t.verifyWechat}
            value={wechat}
            onChange={(e) => setWechat(e.target.value)}
          />
          <p style={hint}>{t.verifyContactHint}</p>
        </div>

        <div style={{ margin: '20px 0' }}>
          <label style={label} htmlFor="verify-cat">{t.verifyCategory}</label>
          <select id="verify-cat" style={input} value={speciality} onChange={(e) => setSpeciality(e.target.value)}>
            <option value="">—</option>
            {cats.map((c) => <option key={c.val} value={c.val}>{c.label}</option>)}
          </select>
        </div>

        <div>
          <label style={label} htmlFor="verify-store">{t.verifyStore}</label>
          <input id="verify-store" style={input} value={tradeLink} onChange={(e) => setTradeLink(e.target.value)} />
          <p style={hint}>{t.verifyStoreHint}</p>
        </div>
      </div>

      <button style={{ ...btn, opacity: busy ? 0.6 : 1 }} onClick={submit} disabled={busy || uploading}>
        {busy ? t.verifySubmitting : t.verifySubmit}
      </button>
      {err && <p style={errText}>{err}</p>}

      <button style={ghost} onClick={() => nav('/dashboard')}>{t.verifySkip}</button>
      <p style={{ ...hint, textAlign: 'center' }}>{t.verifySkipNote}</p>
    </div></div>
  );
}
